import type { SessionMethodName, SessionMethodParams, SessionMethodReturn, SupportedSession } from "../types";
import { parseISOStrings } from "@tellescope/type-correction";

/**
 * Arguments required to execute a Tellescope session method.
 *
 * @template Method - The session method name (e.g., 'sync', 'authenticate')
 *
 * @property session - The Tellescope SDK session instance
 * @property method - The name of the session method to execute
 * @property request - The parameters required for the specific session method
 * @property parseISOStrings - When true, automatically converts ISO 8601 date strings to Date objects in the response
 */
export interface ExecuteSessionMethodArgs<Method extends SessionMethodName> {
  session: SupportedSession;
  method: Method;
  request: SessionMethodParams<Method>;
  parseISOStrings?: boolean;
}

/**
 * Executes a Tellescope session method dynamically based on the provided method name.
 *
 * This utility function provides type-safe access to the Tellescope SDK session methods,
 * validating that the method exists and is callable before execution. Optionally, it can
 * automatically convert ISO 8601 date strings in the response to Date objects.
 *
 * @template Method - The session method name
 *
 * @param args - The execution arguments containing session, method, request, and optional parseISOStrings flag
 * @param args.parseISOStrings - When true, converts ISO strings to Date objects with keepType=true, deep=true, deepArrays=true (default: false)
 * @returns A promise resolving to the session method's return value
 *
 * @throws {Error} If the specified method is not found on the session
 * @throws {Error} If the specified method is not a function
 *
 * @example
 * ```typescript
 * const result = await executeTellescopeSessionMethod({
 *   session: tellescopeSession,
 *   method: 'sync',
 *   request: { from: new Date('2024-01-01') }
 * });
 *
 * // With automatic date parsing
 * const resultWithDates = await executeTellescopeSessionMethod({
 *   session: tellescopeSession,
 *   method: 'test_authenticated',
 *   request: undefined,
 *   parseISOStrings: true
 * });
 * ```
 */
export const executeTellescopeSessionMethod = async <Method extends SessionMethodName>({
  session,
  method,
  request,
  parseISOStrings: shouldParseISOStrings = false,
}: ExecuteSessionMethodArgs<Method>): Promise<SessionMethodReturn<Method>> => {
  // @ts-expect-error - Dynamic access on union of Session | EnduserSession; method may only exist on one side. Runtime validation below handles missing methods.
  const sessionMethod = session[method];

  if (!sessionMethod) {
    throw new Error(`Method ${String(method)} not found on session`);
  }

  if (typeof sessionMethod !== "function") {
    throw new Error(`Session method ${String(method)} is not a function`);
  }

  let result: SessionMethodReturn<Method> | undefined = undefined;

  if (method === "authenticate") {
    const typedRequest = request as ExecuteSessionMethodArgs<"authenticate">["request"];
    result = (await sessionMethod(typedRequest.email, typedRequest.password)) as SessionMethodReturn<Method>;
  } else {
    result = await sessionMethod(request);
  }

  if (result === undefined) {
    throw new Error("Error executing session method");
  }

  // Return the result, optionally parsing ISO strings to Date objects
  if (shouldParseISOStrings) {
    return parseISOStrings(result, { keepType: true, deep: true, deepArrays: true });
  }

  return result;
};
